import React, { useCallback, useEffect, useLayoutEffect, useRef } from "react";
import { useCreditSearchQuery } from "../creditSearch";
import { useCanvasScene } from "./buildScene";
import { hitTestScene } from "./layout/scene";
import { imageStore } from "./assets";
import { paintScene } from "./paint/paintScene";

const readEndHold = (node) => {
  if (!node?.classList?.contains("previewEndHoldTarget")) return null;
  const offset = parseFloat(node.style.getPropertyValue("--preview-end-hold-offset"));
  const opacity = parseFloat(node.style.getPropertyValue("--preview-end-hold-opacity"));
  return {
    offset: Number.isFinite(offset) ? offset : 0,
    opacity: Number.isFinite(opacity) ? opacity : 1,
  };
};

/**
 * Canvas-rendered scroll preview. The canvas is a sticky, viewport-sized
 * layer inside #preview; below it, transparent placeholders with the same
 * ids/classes as the DOM preview (#view_N.blockWrapper, blank screens) keep
 * scrollHeight, scrollTo centering, playback and end-hold working
 * unchanged. Every frame the visible slice of the scene is repainted.
 */
export default function CanvasPreview({
  credits,
  settings,
  fontStyle,
  previewRef,
  canonicalWidth,
  viewScale,
  blankHeight,
  blankColor,
  currentBlockIdx,
  showFocus,
  focusOverlayColor,
  focusBorderColor,
  onSelect,
}) {
  const canvasRef = useRef(null);
  const wrapperRef = useRef(null);
  const placeholdersRef = useRef(null);
  const searchQuery = useCreditSearchQuery();
  const { scene, sceneVersion } = useCanvasScene({
    credits,
    settings,
    fontStyle,
    blankHeight,
    width: canonicalWidth,
  });

  const paintStateRef = useRef({});
  paintStateRef.current = {
    scene,
    sceneVersion,
    focus: showFocus && currentBlockIdx != null
      ? { idx: currentBlockIdx, overlayColor: focusOverlayColor, borderColor: focusBorderColor }
      : null,
    query: searchQuery,
  };

  useLayoutEffect(() => {
    const canvas = canvasRef.current;
    const cssHeight = Math.max(1, previewRef.current?.clientHeight || 1);
    if (!canvas) return;
    canvas.style.height = `${cssHeight}px`;
    canvas.style.marginBottom = `${-cssHeight}px`;
  }, [previewRef]);

  useEffect(() => {
    let raf = null;
    let lastKey = "";
    let lastScene = null;

    const paint = () => {
      raf = requestAnimationFrame(paint);
      const host = previewRef.current;
      const canvas = canvasRef.current;
      const wrapper = wrapperRef.current;
      if (!host || !canvas || !wrapper) return;
      const state = paintStateRef.current;

      const cssHeight = Math.max(1, host.clientHeight);
      if (canvas.style.height !== `${cssHeight}px`) {
        canvas.style.height = `${cssHeight}px`;
        canvas.style.marginBottom = `${-cssHeight}px`;
      }

      const canvasRect = canvas.getBoundingClientRect();
      const wrapperRect = wrapper.getBoundingClientRect();
      const scale = wrapperRect.width / canonicalWidth || viewScale || 1;
      const dpr = window.devicePixelRatio || 1;
      const cssWidth = Math.max(1, Math.round(canvasRect.width));
      const lastPlaceholder =
        placeholdersRef.current?.lastElementChild?.previousElementSibling;
      const endHold = readEndHold(lastPlaceholder);
      const dx = wrapperRect.left - canvasRect.left;
      const dy = wrapperRect.top - canvasRect.top;

      const key = [
        state.sceneVersion,
        dx.toFixed(2),
        dy.toFixed(2),
        scale,
        dpr,
        cssWidth,
        cssHeight,
        state.focus?.idx,
        state.focus?.overlayColor,
        state.focus?.borderColor,
        state.query,
        endHold?.offset,
        endHold?.opacity,
      ].join("|");
      if (key === lastKey && lastScene === state.scene) return;
      lastKey = key;
      lastScene = state.scene;

      const deviceWidth = Math.round(cssWidth * dpr);
      const deviceHeight = Math.round(cssHeight * dpr);
      if (canvas.width !== deviceWidth) canvas.width = deviceWidth;
      if (canvas.height !== deviceHeight) canvas.height = deviceHeight;
      const ctx = canvas.getContext("2d");
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.clearRect(0, 0, deviceWidth, deviceHeight);
      ctx.setTransform(dpr * scale, 0, 0, dpr * scale, dx * dpr, dy * dpr);

      const yFrom = -dy / scale;
      const yTo = yFrom + cssHeight / scale;
      paintScene(ctx, state.scene, {
        yFrom,
        yTo,
        images: imageStore,
        focus: state.focus,
        endHold: endHold
          ? { offset: endHold.offset, opacity: endHold.opacity }
          : null,
        query: state.query,
      });
    };

    raf = requestAnimationFrame(paint);
    return () => cancelAnimationFrame(raf);
  }, [previewRef, canonicalWidth, viewScale]);

  const pointFromEvent = useCallback(
    (event) => {
      const wrapper = wrapperRef.current;
      if (!wrapper) return null;
      const rect = wrapper.getBoundingClientRect();
      const scale = rect.width / canonicalWidth || 1;
      return {
        x: (event.clientX - rect.left) / scale,
        y: (event.clientY - rect.top) / scale,
      };
    },
    [canonicalWidth],
  );

  const handleClick = useCallback(
    (event) => {
      const point = pointFromEvent(event);
      if (!point) return;
      const hit = hitTestScene(scene, point.x, point.y);
      if (!hit?.credit) return;
      if (hit.logo && typeof window !== "undefined") {
        window.dispatchEvent(
          new CustomEvent("scrollx:logo-select", { detail: hit.logo.detail }),
        );
      }
      onSelect?.(hit.credit.id, hit.credit.idx);
    },
    [onSelect, pointFromEvent, scene],
  );

  const handleMouseMove = useCallback(
    (event) => {
      const wrapper = wrapperRef.current;
      const point = pointFromEvent(event);
      if (!wrapper || !point) return;
      const hit = hitTestScene(scene, point.x, point.y);
      const cursor = hit?.logo ? "pointer" : hit?.credit ? "default" : "";
      if (wrapper.style.cursor !== cursor) wrapper.style.cursor = cursor;
    },
    [pointFromEvent, scene],
  );

  const blankStyle = {
    width: canonicalWidth,
    height: `${blankHeight}px`,
    backgroundColor: blankColor || "transparent",
  };

  return (
    <>
      <canvas
        ref={canvasRef}
        className="canvasPreviewSurface"
        aria-hidden="true"
        data-html2canvas-ignore="true"
        style={{
          position: "sticky",
          top: 0,
          display: "block",
          flexShrink: 0,
          width: canonicalWidth * viewScale,
          pointerEvents: "none",
        }}
      />
      <div
        id="preview-zoom-wrapper"
        ref={wrapperRef}
        data-canvas-preview="true"
        onClick={handleClick}
        onMouseMove={handleMouseMove}
        style={{
          width: canonicalWidth,
          margin: "0 auto",
          zoom: viewScale,
          flexShrink: 0,
        }}
      >
        <div ref={placeholdersRef}>
          <div
            className="previewBlankScreen previewBlankScreen--start"
            aria-hidden="true"
            style={blankStyle}
          />
          {scene.wrappers.map((wrapper) => (
            <div
              key={wrapper.idx}
              id={"view_" + wrapper.idx}
              className="blockWrapper pr-prants"
              data-credit-block-index={wrapper.idx}
              style={{ width: canonicalWidth, height: `${wrapper.height}px` }}
            />
          ))}
          <div
            className="previewBlankScreen previewBlankScreen--end"
            aria-hidden="true"
            style={blankStyle}
          />
        </div>
      </div>
    </>
  );
}
